import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext({
  cartItems: [],
  designCart: [], // 👈 Added for local storage (ToolBar)
  addToCart: () => {},
  addDesignToCart: () => {}, // 👈 New function
  removeFromCart: () => {},
  removeFromDesignCart: () => {}, // 👈 New function
  increment: () => {},
  decrement: () => {},
  clearCart: () => {},
  clearDesignCart: () => {}, // 👈 New function
  total: 0,
});

// Cookie utility functions
const cookieUtils = {
  setCookie: (name, value, days = 30) => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    const path = "path=/";
    const sameSite = "SameSite=Strict";
    document.cookie = `${name}=${encodeURIComponent(
      value
    )}; ${expires}; ${path}; ${sameSite}`;
  },

  getCookie: (name) => {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  },

  deleteCookie: (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  },
};

export const CartProvider = ({ children }) => {
  // 🧩 Normal cart (cookies)
  const [cartItems, setCartItems] = useState(() => {
    const storedCart = cookieUtils.getCookie("cartItems");
    try {
      return storedCart ? JSON.parse(storedCart) : [];
    } catch (error) {
      console.error("Failed to parse cart from cookie:", error);
      return [];
    }
  });

  // 🧩 Local storage design cart
  const [designCart, setDesignCart] = useState(() => {
    try {
      const stored = localStorage.getItem("designCart");
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Failed to parse design cart:", error);
      return [];
    }
  });

  // 🧩 Persist both storages
  useEffect(() => {
    cookieUtils.setCookie("cartItems", JSON.stringify(cartItems), 30);
    console.log("Cart updated (cookies):", cartItems);
  }, [cartItems]);

  useEffect(() => {
    localStorage.setItem("designCart", JSON.stringify(designCart));
    console.log("Design cart updated (localStorage):", designCart);
  }, [designCart]);

  // 🧩 Normal cart add
  // 🧩 Normal cart add
  const addToCart = (item, quantity = 1, selectedColor = null) => {
    if (!item) return;
    const itemId = item._id || item.id;
    if (!itemId) return;

    // ✅ Pick color info if available
    let colorInfo = null;
    let image = "";

    if (selectedColor && item.colors?.length) {
      // Find the color that matches (case-insensitive comparison)
      colorInfo = item.colors.find(
        (c) =>
          c.name.toLowerCase().trim() === selectedColor.toLowerCase().trim()
      );

      // If no match found, log warning but don't fallback
      if (!colorInfo) {
        console.warn(`Color "${selectedColor}" not found in product colors`);
        return; // Don't add to cart if color doesn't exist
      }
    } else if (item.colors?.length) {
      // Only use first color if NO color was selected at all
      colorInfo = item.colors[0];
    }

    if (colorInfo?.images?.length) {
      image = colorInfo.images[0]; // first image of selected color
    }

    const colorData = colorInfo
      ? { name: colorInfo.name, hex: colorInfo.hex }
      : null;

    setCartItems((prev) => {
      // Match product + color combination
      const existing = prev.find(
        (i) => i.id === itemId && i.color?.name === colorData?.name
      );

      if (existing) {
        return prev.map((i) =>
          i.id === itemId && i.color?.name === colorData?.name
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }

      const newItem = {
        id: itemId,
        name: item.title || item.name || "Unnamed Product",
        price: parseFloat(item.price) || 0,
        image,
        description: item.description || item.subtitle || "",
        quantity: Math.max(1, parseInt(quantity)),
        categoryId:
          item.category?._id || item.category?.id || item.category || null,
        categoryName: item.category?.name || "Unknown",
        color: colorData, // ✅ store selected color info
      };

      return [...prev, newItem];
    });
  };

  // 🧩 LocalStorage Add for T-Shirt Designs
  const addDesignToCart = (designItem) => {
    if (!designItem?.id) {
      console.error("Design item missing ID");
      return;
    }

    setDesignCart((prev) => {
      const exists = prev.find((d) => d.id === designItem.id);
      if (exists) {
        // Replace the existing one (overwrite same design)
        return prev.map((d) => (d.id === designItem.id ? designItem : d));
      }
      return [...prev, designItem];
    });
  };

  // 🧩 Removal and clearing for both
  const removeFromCart = (id) =>
    setCartItems((prev) => prev.filter((i) => i.id !== id));
  const removeFromDesignCart = (id) =>
    setDesignCart((prev) => prev.filter((i) => i.id !== id));

  const increment = (id) =>
    setCartItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity: i.quantity + 1 } : i))
    );

  const decrement = (id) =>
    setCartItems((prev) =>
      prev
        .map((i) =>
          i.id === id && i.quantity > 1 ? { ...i, quantity: i.quantity - 1 } : i
        )
        .filter((i) => i.quantity > 0)
    );

  const clearCart = () => {
    setCartItems([]);
    cookieUtils.deleteCookie("cartItems");
  };

  const clearDesignCart = () => {
    setDesignCart([]);
    localStorage.removeItem("designCart");
  };

  const total = cartItems.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        designCart, // 👈 Access this in ToolBar or design page
        addToCart,
        addDesignToCart,
        removeFromCart,
        removeFromDesignCart,
        increment,
        decrement,
        clearCart,
        clearDesignCart,
        total,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

export default CartContext;
